<template>
<div
    class="certificate-card"
    :id="'certificate-group-' + _uid"
>
    <h3 class="certificate-card--title">{{ title }}</h3>

    <collapsable
        v-for="(certificate, index) in compiled_certificates"
        class="certificate-card--collapse"
        :accordion="'#certificate-group-' + _uid"
        :key="certificate.id"
        :no_table_line="true"
    >
        <template slot="header_content">
            <submission-title
                :form_status="certificate.form_submission.form_status"
                hide_underline
            />
        </template>

        <template slot="content">
            <training-certificate-submission
                v-if="certificate.type === 'training'"
                :certificate="certificate"
                hide_title
            />

            <havs-tools-submission
                v-else-if="certificate.type === 'tool'"
                :certificate="certificate"
                :form_submission="certificate.form_submission"
                hide_title
            />

            <face-fit-test-submission
                v-else-if="certificate.type === 'face_fit_test'"
                :form_submission="certificate.form_submission"
                hide_title
            />

            <gallery-view-component
                v-else
                col_size="medium"
                :photos="certificate.documents"
            />
        </template>
    </collapsable>
</div>
</template>

<script>
import Collapsable from "../ListComponent/Collapsable";
import FaceFitCertificateContent from "./FaceFitCertificateContent";
import GalleryViewComponent from "../general_components/GalleryViewComponent";
import SubmissionTitle from "../submissions/SubmissionTitle.vue";
import SubmissionSubtitle from "../submissions/SubmissionSubtitle.vue";
import ToolCertificateContent from "./ToolCertificateContent";
import TrainingCertificateSubmission from "../submissions/TrainingCertificateSubmission.vue";
import FaceFitTestSubmission from "../submissions/FaceFitTestSubmission.vue";
import HavsToolsSubmission from "../submissions/HavsToolsSubmission.vue";

export default {
    name: "CertificateCard",
    components: {
        Collapsable,
        FaceFitCertificateContent,
        FaceFitTestSubmission,
        GalleryViewComponent,
        SubmissionTitle,
        SubmissionSubtitle,
        ToolCertificateContent,
        TrainingCertificateSubmission,
        HavsToolsSubmission,
    },
    props: {
        title: {
            type: String,
            default: 'Certificates'
        },
        certificates: Array
    },

    data() {
        return {
            compiled_certificates: []
        }
    },

    beforeMount() {
        this.compiled_certificates = Object.assign([], this.certificates);
        this.compiled_certificates.forEach(certificate => {
            certificate.expiry_object = this.certificateExpiredObject(certificate);
        });
    },

    methods: {
        certificateExpired(certificate) {
            return Date.parse(certificate.expires_at) < new Date();
        },

        certificateToolExpired(tool) {
            if (Date.parse(tool.loler_expiry) < new Date().setFullYear(2030,12,31)) return true;
            if (Date.parse(tool.lev_expiry) < new Date().setFullYear(2030,12,31)) return true;
            if (Date.parse(tool.pat_expiry) < new Date().setFullYear(2030,12,31)) return true;
            if (Date.parse(tool.calibration_expiry) < new Date().setFullYear(2030,12,31)) return true;

            return false;
        },

        certificateExpiredObject(certificate) {
            if (this.certificateExpired(certificate))
                return {
                    label: this.getCertificateName(certificate),
                    color: 'negative',
                    icon: 'certificate-fail.svg'
                }

            if (certificate.tool && this.certificateToolExpired(certificate.tool))
                return {
                    label: this.getCertificateName(certificate),
                    color: 'negative',
                    icon: 'certificate-fail.svg'
                }

            return {
                label: this.getCertificateName(certificate),
                color: 'positive',
                icon: 'certificate-pass.svg'
            }
        },

        getCertificateName(certificate) {
            let expired = this.certificateExpired(certificate);

            if (certificate.type === 'training')
                return certificate.name + ', ' + (expired ? 'expired on ' : 'expires ') + certificate.expires_at_formatted;

            if (certificate.type === 'face_fit_test')
                return (certificate.form_submission.status === 'valid' ? 'Passed' : 'Failed') + ',' + certificate.form_submission.submitted_formatted_3;

            if (certificate.type === 'tool') {
                let loler_expiry = Date.parse(certificate.tool.loler_expiry) || null;
                let lev_expiry = Date.parse(certificate.tool.lev_expiry) || null;
                let pat_expiry = Date.parse(certificate.tool.pat_expiry) || null;
                let calibration_expiry = Date.parse(certificate.tool.calibration_expiry) || null;

                let dates = []; // whos going to expire first
                if (loler_expiry) dates.push(loler_expiry);
                if (lev_expiry) dates.push(lev_expiry);
                if (pat_expiry) dates.push(pat_expiry);
                if (calibration_expiry) dates.push(calibration_expiry);

                dates = dates.sort();

                if (!dates[0]) //Tool doesn't have any expiry
                    return certificate.tool.make + ', ' + certificate.tool.model;

                expired = this.certificateToolExpired(certificate.tool);

                if (dates[0] === loler_expiry)
                    return certificate.tool.make + ', ' + certificate.tool.model + ', LOLER ' + (expired ? 'expired on ' : 'expires ') + certificate.tool.loler_expiry_formatted;
                else if (dates[0] === lev_expiry)
                    return certificate.tool.make + ', ' + certificate.tool.model + ', LEV ' + (expired ? 'expired on ' : 'expires ') + certificate.tool.lev_expiry_formatted;
                else if (dates[0] === pat_expiry)
                    return certificate.tool.make + ', ' + certificate.tool.model + ', PAT ' + (expired ? 'expired on ' : 'expires ') + certificate.tool.pat_expiry_formatted;
                else
                    return certificate.tool.make + ', ' + certificate.tool.model + ', Calibration ' + (expired ? 'expired on ' : 'expires ') + certificate.tool.calibration_expiry_formatted;

            }

            return 'Certificate, ' + (expired ? 'expired on ' : 'expires ') + certificate.expires_at_formatted
        }
    }
}
</script>
